import { useCallback } from 'react';

import { INPUT_METHOD } from '@atlaskit/editor-common/analytics';
import type { EditorActions } from '@atlaskit/editor-core';

import { useFullPageEditorApi } from '@confluence/full-page-editor-store';
import {
	useSmartCreationModalActions,
	type OpenModalParams,
} from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';

/**
 * Returns an `openSmartCreationModal` callback that, when called from the
 * `editorQuickInsert` entry point, wraps the submit callback to also insert
 * a hyperlink into the editor at the current cursor position.
 */
export const useOpenSmartCreationModalFromQuickInsert = (editorActions: EditorActions) => {
	const [{ fullPageEditorPresetAPI }] = useFullPageEditorApi();
	const { openModal: openSmartCreationModalBase } = useSmartCreationModalActions();

	return useCallback(
		({ entryPoint, spaceKey, parentContent, content }: OpenModalParams) => {
			if (entryPoint === 'editorQuickInsert' && fullPageEditorPresetAPI) {
				const submitWithInsertLink = async ({
					baseUrl,
					path,
				}: {
					baseUrl: string;
					path: string;
				}) => {
					const fullUrl = baseUrl + path;
					const view = editorActions._privateGetEditorView?.();

					// insert hyperlink at the current cursor position
					if (view && fullPageEditorPresetAPI?.hyperlink?.actions?.insertLink) {
						const { from } = view.state.selection;
						const cmd = fullPageEditorPresetAPI.hyperlink.actions.insertLink(
							INPUT_METHOD.TYPEAHEAD, // inputMethod — triggered via the `/` quick insert typeahead menu
							from, // from — start position at current cursor
							from, // to — same as from, no text selected
							fullUrl, // href — the URL to link to
							undefined, // title — no title, falls back to displayText
							fullUrl, // displayText — show the URL as the link text in the editor
							true, // cardsAvailable — attempt to resolve as a smart link
						);
						cmd(view.state, view.dispatch);
					}
				};

				openSmartCreationModalBase({
					entryPoint,
					spaceKey,
					parentContent,
					content,
					submitCallback: submitWithInsertLink,
				});
			}
		},
		[openSmartCreationModalBase, fullPageEditorPresetAPI, editorActions],
	);
};
